export const DEFAULT_ERROR_MESSAGE = "We couldn't complete that request. Please try again. If the problem continues, contact your administrator.";

// Implementation details from browsers, scanners, proxies, .NET, SQL and services.
const technicalDetails = [
  /\b(?:[\w.]+Exception|TypeError|ReferenceError|SyntaxError|FetchApiError|AxiosError|QuotaExceededError|InvalidStateError|NotAllowedError|AbortError)\b|\b(?:System|Microsoft)\.[\w.]+/i,
  /\b(?:stack trace|stacktrace|inner exception|BytePositionInLine|LineNumber)\s*:|\n\s*at\s+/i,
  /\b(?:The JSON value could not be converted|Unexpected token|Unexpected end of JSON|is not valid JSON|JSON\.parse|Unexpected character encountered while parsing|Guid should contain)\b/i,
  /\b(?:Object reference not set|Sequence contains no|Value cannot be null|Input string was not in a correct format|Cannot read propert(?:y|ies)|is not a function|is not defined)\b/i,
  /\b(?:Format of the initialization string|Login failed for user|Cannot insert duplicate key|An error occurred while saving the entity changes|Invalid column name|Invalid object name|Incorrect syntax near|Violation of .* constraint|Execution Timeout Expired|A network-related or instance-specific error|ECONN\w+|ENOTFOUND|EHOSTUNREACH|SQLSTATE)\b/i,
  /<!doctype\s+html|<html\b|<body\b|\b[a-z]:\\|\/(?:app|home|usr|var|tmp)\//i
];
const loggedObjects = new WeakSet<object>();
const loggedMessages = new Set<string>();

function logDiagnostic(error: unknown, raw: string): void {
  if (error && typeof error === 'object') {
    if (loggedObjects.has(error)) return;
    loggedObjects.add(error);
  } else {
    if (loggedMessages.has(raw)) return;
    if (loggedMessages.size >= 100) loggedMessages.clear();
    loggedMessages.add(raw);
  }
  // Do not log request configuration, headers, tokens, or request bodies.
  console.error('Application error details:', raw, error instanceof Error ? error.stack : undefined);
}

export function errorMessageForStatus(status: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return "You don't have permission to do that. Contact your administrator if you need access.";
  if (status === 404) return 'This item is no longer available. Refresh the page and try again.';
  if (status === 409) return 'This item has changed. Refresh the page before trying again.';
  if (status === 413) return 'This file is too large. Choose a smaller file and try again.';
  if (status === 429) return 'Too many requests were made. Please wait a moment and try again.';
  if (status === 'FETCH_ERROR') return "We couldn't connect. Check your connection and try again.";
  if (status === 'TIMEOUT_ERROR' || status === 408 || status === 504) return 'This is taking longer than expected. Please try again.';
  return fallback;
}

/** Format at the display boundary; retain the original error for diagnostics. */
export function simplifyError(err: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  if (!err) return fallback;
  const value = err as any;
  const status = value?.status ?? value?.response?.status;
  const body = value?.data ?? value?.response?.data;
  const validation = body?.errors && typeof body.errors === 'object'
    ? Object.values(body.errors).flat().filter(item => typeof item === 'string').join(' ')
    : undefined;
  const candidates = [body?.error, body?.message, body?.detail, body?.details, validation, body?.title, value?.error, value?.message,
    typeof body === 'string' ? body : undefined, typeof err === 'string' ? err : undefined];
  const raw = candidates.find(candidate => typeof candidate === 'string' && candidate.trim()) as string | undefined;
  const friendlyFallback = errorMessageForStatus(status, fallback);
  if (!raw) return friendlyFallback;
  if (/^(?:TypeError:\s*)?(?:failed to fetch|network\s*error|network request failed|load failed)\.?$/i.test(raw.trim())) {
    logDiagnostic(err, raw);
    return errorMessageForStatus('FETCH_ERROR');
  }
  if ((typeof status === 'number' && status >= 500) || technicalDetails.some(pattern => pattern.test(raw)) ||
      (err instanceof Error && err.name !== 'Error') || raw.length > 800 ||
      /^\s*[\[{]/.test(raw) || /^(?:Unknown error|Internal Server Error|Bad Request|Request failed with status code \d+)\.?$/i.test(raw.trim())) {
    logDiagnostic(err, raw);
    return friendlyFallback;
  }
  return raw.trim();
}

/** Sanitize failed responses only. Preserve status, codes, field keys and conflict
 * metadata; successful responses and administrative logs never go through here. */
export function sanitizeErrorResponse(data: unknown, status: unknown): unknown {
  const fallback = errorMessageForStatus(status);
  const visit = (value: unknown, isMessage = false, validation = false): unknown => {
    if (typeof value === 'string') return isMessage ? simplifyError({ status, message: value }, fallback) : value;
    if (Array.isArray(value)) return value.map(item => visit(item, isMessage, validation));
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key,
      !validation && /^(code|status|traceId|type|instance|name|field)$/i.test(key) ? item :
        visit(item, isMessage || /^(message|error|errors|errorMessage|errorDetails|detail|details|title|exception|stack|stackTrace)$/i.test(key), validation || key === 'errors')]));
  };
  if (typeof data === 'string') return { message: simplifyError({ status, message: data }, fallback) };
  return data ? visit(data, Array.isArray(data)) : { message: fallback };
}
